import classNames from 'classnames'
import React, {Fragment, memo, useCallback, useEffect, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import {Input, Spinner} from 'reactstrap'
import {ReactComponent as IconCheck} from '../../../../../../assets/icons/check.svg'
import {ReactComponent as Trash} from '../../../../../../assets/icons/trash-alt.svg'
import {ReactComponent as Transparent} from '../../../../../../assets/icons/transparent.svg'
import {ReactComponent as IconSearch} from '../../../../../../assets/icons/search.svg'
import EmptyList from '../../../../../../assets/images/empty-list.png'
import {
    deleteDesignLibraryImage,
    getDesignLibraryImage,
    getListDesignLibrary,
} from '../../../../../../services/ArtworkTemplateServices'
import {confirmModal} from '../../../../../../shared/PFConfirmModal'
import {toaster} from '../../../../../../shared/PFToast'
import {useSelectedVariantContext} from '../../../../context/SelectedVariantContext'
import {
    actionUpdateBgImage, actionUpdateBgImageType,
    actionUpdateListBbImages, actionUpdateLoadingBackground
} from '../../../../context/SelectedVariantContext/action'
import {isBackofficeEnv as checkEnv} from '../../../../../../helpers/isBackoffceEnv'
import {getLocalData, setLocalData} from '../../../../../../services/StorageServices'
import randomstring from 'randomstring'
import {getInfomationBackground} from '../../../../../../helpers/createProductCombine'

const UploadByAssets = (props) => {
    const {
        state: {listBackgroundImages, currentBgImage, currentBgImageType},
        dispatch
    } = useSelectedVariantContext()
    const {toggle, wPreview = 600, hPreview = 600} = props
    const [total, setTotal] = useState(0)
    const [loadingFetch, setLoadingFetch] = useState(false)
    const [selectedImage, setSelectedImage] = useState('')
    const [listAssets, setListAssets] = useState([])
    const [hasMore, setHasMore] = useState(true)
    const [params, setParams] = useState({
        name: '',
        sort_key: 'created_at',
        sort_order: 'desc',
        page: 1,
        limit: 20,
    })
    const [pages, setPages] = useState(0)
    const [activeTab, setActiveTab] = useState('mockup-files') // 'mockup-files' or 'ai-background'
    let _timeOut = null
    const [clickTimeout, setClickTimeout] = useState(null);

    const getWindowWidth = () => {
        const {innerWidth: width} = window
        return width
    }

    const [windowWidth, setWindowWidth] = useState(getWindowWidth())
    const isBackofficeEnv = checkEnv()

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(getWindowWidth())
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const _fetchDesignLibrary = useCallback(async () => {
        try {
            setLoadingFetch(true)
            if (isBackofficeEnv) {
                // For 'mockup-files' tab, don't send is_ai_background parameter to get items where field doesn't exist OR equals false
                const apiParams = {...params}
                if (activeTab === 'mockup-files') {
                    delete apiParams.is_ai_background
                }
                
                const {success, data, message} = await getListDesignLibrary(apiParams)
                if (!success) throw new Error(message)
                const {images, pages, total} = data
                
                // Filter by type based on active tab (get items where is_ai_background doesn't exist OR equals false)
                const filteredImages = images.filter((img) => {
                    if (activeTab === 'ai-background') {
                        return img.is_ai_background === true
                    } else {
                        // Get items where is_ai_background doesn't exist OR equals false
                        return img.is_ai_background === undefined || img.is_ai_background === null || img.is_ai_background === false
                    }
                })
                
                setTotal(filteredImages.length)
                setPages(pages)
                setHasMore(params.page < pages)
                setListAssets(filteredImages)
            } else {
                setHasMore(false)
                // Get fresh designLibrary data
                const designLibrary = getLocalData('designLibrary') || []
                // Filter by type based on active tab
                let filteredDesign = designLibrary.filter((obj) => {
                    const matchesName = obj.name.includes(params.name)
                    const matchesType = activeTab === 'ai-background' 
                        ? obj.is_ai_background === true 
                        : !obj.is_ai_background
                    return matchesName && matchesType
                })
                filteredDesign.sort((a, b) => {
                    if (params.sort_order === 'asc') {
                        return a.name.localeCompare(b.name)
                    }
                    return b.name.localeCompare(a.name)
                })
                setListAssets(filteredDesign)
                setTotal(filteredDesign.length)
            }
        } catch (error) {
            toaster({
                type: 'error',
                message: error.message,
            })
        } finally {
            setLoadingFetch(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.name, params.sort_order, activeTab])

    useEffect(() => {
        _fetchDesignLibrary()
    }, [_fetchDesignLibrary])

    const _next = async () => {
        const newPage = params.page + 1
        try {
            if (newPage > pages) return setHasMore(false)
            
            // For 'mockup-files' tab, don't send is_ai_background parameter to get items where field doesn't exist OR equals false
            const apiParams = {...params, page: newPage}
            if (activeTab === 'mockup-files') {
                delete apiParams.is_ai_background
            }
            
            const {success, data, message} = await getListDesignLibrary(apiParams)
            if (!success) throw new Error(message)
            const {images, pages: responsePages} = data
            setPages(responsePages)
            
            // Filter by type based on active tab (get items where is_ai_background doesn't exist OR equals false)
            const filteredImages = images.filter((img) => {
                if (activeTab === 'ai-background') {
                    return img.is_ai_background === true
                } else {
                    // Get items where is_ai_background doesn't exist OR equals false
                    return img.is_ai_background === undefined || img.is_ai_background === null || img.is_ai_background === false
                }
            })
            
            const concatAssets = [...listAssets, ...filteredImages]
            setListAssets(concatAssets)
        } catch (error) {
            toaster({
                type: 'error',
                message: error.message,
            })
        } finally {
            setParams((prev) => ({...prev, page: newPage}))
        }
    }

    const _fetchRegularDesignImage = async (imageId) => {
        try {
            const {data, success, message} = await getDesignLibraryImage(imageId)
            if (!success) throw new Error(message)
            return data
        } catch (error) {
            toaster({
                type: 'error',
                message: error.message,
            })
        }
    }

    const _handleSearch = (event) => {
        const {name, value} = event.target
        if (_timeOut) clearTimeout(_timeOut)

        _timeOut = setTimeout(() => {
            setParams((prev) => ({...prev, [name]: value ? value.trim() : '', page: 1}))
        }, 500)
    }

    const _handleDeleteImage = (imageId) => {
        const _deleteImage = async () => {
            try {
                if (isBackofficeEnv) {
                    const {success, message} = await deleteDesignLibraryImage(imageId)
                    if (!success) throw new Error(message)
                    _fetchDesignLibrary()
                } else {
                    const _listAssets = Object.assign([], listAssets)
                    if (_listAssets.length > 0) {
                        const availableDesigns = _listAssets.filter((design) => {
                            return design._id !== imageId
                        })
                        setLocalData('designLibrary', availableDesigns)
                        setListAssets(availableDesigns)
                    }
                }
            } catch (error) {
                toaster({
                    type: 'error',
                    message: error.message,
                })
            }
        }

        confirmModal({
            confirmText: 'Delete',
            confirmColor: 'danger',
            title: 'Confirm Delete image',
            message: 'Are you sure you want to delete this image?',
            onOk: () => _deleteImage(),
        })
    }

    const _onAddImage = async (selectedImage) => {
        try {
            toggle()
            let imageObj = null
            if (isBackofficeEnv) {
                const regularImage = await _fetchRegularDesignImage(selectedImage)
                const {origin_url, thumb_url, name, size, mimetype, width, height, s3_path} = regularImage
                imageObj = {
                    name,
                    _id: randomstring.generate(7),
                    src: origin_url,
                    tinySrc: thumb_url,
                    naturalSize: size,
                    imageType: mimetype,
                    naturalWidth: width,
                    naturalHeight: height,
                    created: new Date(),
                    key: s3_path
                }
            } else {
                imageObj = listAssets.find((asset) => asset._id === selectedImage)
            }
            await dispatch(actionUpdateListBbImages([...listBackgroundImages, {
                file: imageObj?.src,
                key: imageObj?.key,
                naturalWidth: imageObj?.naturalWidth,
                naturalHeight: imageObj?.naturalHeight,
            }]))
            const info = getInfomationBackground(currentBgImageType, imageObj)
            const {width, height, x, y} = info
            const backgroundImage = {
                id: `backgroundImage`,
                ...currentBgImage,
                naturalWidth: imageObj.naturalWidth,
                naturalHeight: imageObj.naturalHeight,
                file: imageObj?.src,
                key: imageObj?.key,
                width,
                height,
                x,
                y,
            }

            dispatch(actionUpdateBgImage(backgroundImage))
            dispatch(actionUpdateBgImageType(currentBgImageType))
            dispatch(actionUpdateLoadingBackground(true))
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <div className="UploadByAssets">
            <div className='DesignAsset'>
                <div className='Filter'>
                    <div>
                        <div className='mb-4 d-flex align-items-center gap-4'>
                            <label className='FilesLibraryLabel'>
                                Files in Library ({isBackofficeEnv ? total : listAssets.length})
                            </label>
                            <div className='d-flex align-items-center FilesLibraryTabsButtons'>
                                <button
                                    type="button"
                                    className={classNames('TabButton', {active: activeTab === 'mockup-files'})}
                                    onClick={() => {
                                        setActiveTab('mockup-files')
                                        // Remove is_ai_background from params to get items where field doesn't exist OR equals false
                                        setParams((prev) => {
                                            const newParams = {...prev, page: 1}
                                            delete newParams.is_ai_background
                                            return newParams
                                        })
                                    }}
                                >
                                    Mockup files
                                </button>
                                <button
                                    type="button"
                                    className={classNames('TabButton', {active: activeTab === 'ai-background'})}
                                    onClick={() => {
                                        setActiveTab('ai-background')
                                        setParams((prev) => ({...prev, page: 1, is_ai_background: true}))
                                    }}
                                >
                                    AI background
                                </button>
                            </div>
                        </div>
                        <div className='d-flex align-items-center'>
                            <div className='InputSearch'>
                                <IconSearch/>
                                <Input name='name' id='inputSearch' placeholder='Search...' onChange={_handleSearch} />
                            </div>
                            <div className='SortFilter d-flex align-items-center'>
                                <label>Sort by:</label>
                                <Input
                                    name='sort_order'
                                    type='select'
                                    onChange={_handleSearch}
                                >
                                    <option value='desc'>
                                        Latest - oldest
                                    </option>
                                    <option value='asc'>
                                        Oldest - latest
                                    </option>
                                </Input>
                            </div>
                        </div>
                    </div>
                </div>

                {loadingFetch ? (
                    <div className='ListAssets mt-3'>
                        <Loading />
                    </div>
                ) : (
                    <Fragment>
                        {listAssets.length > 0 ? (
                            <Fragment>
                                {isBackofficeEnv ? (
                                    <InfiniteScroll
                                        next={_next}
                                        hasMore={hasMore}
                                        loader={!loadingFetch && <Loading />}
                                        className='ListAssets mb-3'
                                        dataLength={listAssets.length}
                                        height={windowWidth < 1600 ? 400 : 500}
                                        style={{
                                            padding: '8px',
                                            marginLeft: '-5px'
                                        }}
                                    >
                                        {listAssets.map((asset) => {
                                            const isSelected = selectedImage === asset._id
                                            return (
                                                <AssetItem
                                                    asset={asset}
                                                    isSelected={isSelected}
                                                    isBackofficeEnv={isBackofficeEnv}
                                                    addImage={_onAddImage}
                                                    setSelectedImage={setSelectedImage}
                                                    handleDeleteImage={_handleDeleteImage}
                                                />
                                            )
                                        })}
                                    </InfiniteScroll>
                                ) : (
                                    <div className='ListAssets mb-3'>
                                        {listAssets.map((asset) => {
                                            const isSelected = selectedImage === asset._id
                                            return (
                                                <AssetItem
                                                    asset={asset}
                                                    isSelected={isSelected}
                                                    addImage={_onAddImage}
                                                    isBackofficeEnv={isBackofficeEnv}
                                                    setSelectedImage={setSelectedImage}
                                                    handleDeleteImage={_handleDeleteImage}
                                                />
                                            )
                                        })}
                                    </div>
                                )}
                            </Fragment>
                        ) : (
                            <div className='NoImage'>
                                <div className='text-center'>
                                    <img src={EmptyList} alt='Empty' />
                                    <p><strong className='d-block pb-1 pt-2'>No images yet? Let’s fix that!</strong>Upload
                                        your first image now to kickstart your library</p>
                                </div>
                            </div>
                        )}
                    </Fragment>
                )}
            </div>
        </div>
    )
}

const AssetItem = memo((props) => {
    const {isBackofficeEnv, asset, isSelected, setSelectedImage, addImage, handleDeleteImage} = props

    const {_id, src, thumb_url, name, width, height, size} = asset

    return (
        <Fragment>
            <div
                key={_id}
                className={classNames('AssetItem', {Active: isSelected})}
                onClick={() => setSelectedImage(_id)}
            >
                <div className='position-relative overflow-hidden WrapImage'>
                    <Transparent width={300} height={300}/>
                    <img id="update_image" className="Thumbnail" src={isBackofficeEnv ? thumb_url : src} alt='thumbnail'
                         onClick={() => addImage(_id)} onDoubleClick={(event) => event.preventDefault()}/>
                </div>
                <div className='Info'>
                    <p className='Title'>{name}</p>
                    <p>Size: {(size / 1024 / 1024).toFixed(2)} Mb</p>
                    <p>Resolution: {width} x {height}</p>
                </div>
                {isSelected && (
                    <div className="Mask">
                        <span className="Icon">
                            <IconCheck width={12} height={8} />
                        </span>
                    </div>
                )}
                {!isSelected && (
                    <div
                        className="ButtonDelete cursor-pointer"
                        id="delete_image_library"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            handleDeleteImage(_id)
                        }}
                    >
                        <Trash widths={20} height={20} color="#ffffff" />
                    </div>
                )}
            </div>
        </Fragment>
    )
})

const Loading = () => {
    return (
        <div className="LoadingScroll">
            <Spinner style={{color: '#007bff'}} />
        </div>
    )
}

export default UploadByAssets

